import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type * as React from "react";
import { useState } from "react";

import type { WalletView } from "@/services/clientWallets";
import { orgApiFetch, type PaginatedResponse } from "@/services/orgApiFetch";
interface UseClaimAccountsResult {
    wallets: UseQueryResult<WalletView[], Error>;
    account: WalletView | undefined;
    setSelected: React.Dispatch<React.SetStateAction<string | null>>;
}

export function useClaimAccounts(context: string): UseClaimAccountsResult {
    const [selected, setSelected] = useState<string | null>(null);
    const wallets = useQuery({
        queryKey: ["client-claim-wallets", context],
        queryFn: async () => {
            const result: WalletView[] = [];
            for (let page = 1; page <= 100; page++) {
                const batch = await orgApiFetch<PaginatedResponse<WalletView>>(
                    `/api/org/client/wallets?limit=100&page=${page}`
                );
                result.push(...batch.data);
                if (result.length >= batch.total || !batch.data.length) return result;
            }
            throw new Error("Too many wallets to load. Contact support.");
        }
    });
    return { wallets, account: wallets.data?.find((w) => w.uuid === selected) ?? wallets.data?.[0], setSelected };
}
interface UseClaimDistributionSettingsResult {
    settings: UseQueryResult<{ distributionMode: "stream" | "claim" | null }, Error>;
    mode: UseMutationResult<unknown, Error, "stream" | "claim", unknown>;
}

export function useClaimDistributionSettings(context: string, accountUuid: string): UseClaimDistributionSettingsResult {
    const cache = useQueryClient();
    const queryKey = ["client-distribution-settings", context, accountUuid];
    const path = `/api/org/client/wallets/${accountUuid}/distribution-settings`;
    const settings = useQuery({
        queryKey,
        queryFn: () => orgApiFetch<{ distributionMode: "stream" | "claim" | null }>(path)
    });
    const mode = useMutation({
        mutationFn: (distributionMode: "stream" | "claim") =>
            orgApiFetch(path, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ distributionMode })
            }),
        onSuccess: () => cache.invalidateQueries({ queryKey })
    });
    return { settings, mode };
}
